import React, { useState, useRef, useEffect, useCallback } from 'react';
import './full-screen-camera-capture.scss';
import { compressImages } from '../../utils/imageCompression';
import { CameraPreview } from '@capacitor-community/camera-preview';
import { isNative, base64ToFile } from '../../utils/cameraPreviewHelper';

// Standardized output resolution for all camera captures.
const CAPTURE_MAX_SIDE = 1280;
const CAPTURE_QUALITY = 0.85;

interface FullScreenCameraCaptureProps {
    visible: boolean;
    title: string;
    onImagesUploaded: (files: File[]) => void | Promise<void>;
    onClose: () => void;
}

export function FullScreenCameraCapture({
    visible,
    title,
    onImagesUploaded,
    onClose,
}: FullScreenCameraCaptureProps) {
    const [cameraError, setCameraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const systemCameraInputRef = useRef<HTMLInputElement>(null);
    const stopCameraTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Native camera state ──────────────────────────────────────
    const [nativePreviewActive, setNativePreviewActive] = useState(false);
    const [flashEnabled, setFlashEnabled] = useState(false);
    const nativeStartedRef = useRef(false); // guard against double-start

    // ═══════════════════════════════════════════════════════════
    // WEB PATH (getUserMedia) — unchanged from original
    // ═══════════════════════════════════════════════════════════

    // Fully release camera hardware (web path)
    const stopWebCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    }, []);

    const requestCameraStream = useCallback(async (): Promise<MediaStream> => {
        const constraintsList: MediaStreamConstraints[] = [
            { video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1440 } } },
            { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
            { video: { facingMode: { ideal: 'environment' } } },
            { video: true },
        ];
        let lastError: unknown = null;
        for (const constraints of constraintsList) {
            try {
                return await navigator.mediaDevices.getUserMedia(constraints);
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError;
    }, []);

    // Start the web camera, or reuse an existing stream
    const startOrReattachWebCamera = useCallback(async () => {
        // Cancel any pending shutdown
        if (stopCameraTimeout.current) {
            clearTimeout(stopCameraTimeout.current);
            stopCameraTimeout.current = null;
        }

        // If we already have a live stream, just reattach
        if (streamRef.current && streamRef.current.getVideoTracks().some((t) => t.readyState === 'live')) {
            if (videoRef.current) {
                videoRef.current.srcObject = streamRef.current;
                await videoRef.current.play().catch(() => { });
            }
            return;
        }

        // Otherwise request a new stream
        setCameraError(null);
        try {
            const stream = await requestCameraStream();
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => { });
                await new Promise((resolve) => setTimeout(resolve, 220));
                if (videoRef.current.videoWidth === 0) {
                    stream.getTracks().forEach((t) => t.stop());
                    const retryStream = await navigator.mediaDevices.getUserMedia({ video: true });
                    streamRef.current = retryStream;
                    videoRef.current.srcObject = retryStream;
                    await videoRef.current.play().catch(() => { });
                }
            }
        } catch (err: any) {
            console.error('Camera permissions denied or error:', err);
            setCameraError(
                err?.message || 'لا يمكن فتح الكاميرا، يرجى التحقق من الصلاحيات.',
            );
        }
    }, [requestCameraStream]);

    // Schedule a delayed web camera shutdown (keeps hardware warm for fast transitions)
    const scheduleStopWebCamera = useCallback(() => {
        if (stopCameraTimeout.current) clearTimeout(stopCameraTimeout.current);
        stopCameraTimeout.current = setTimeout(() => {
            stopWebCamera();
        }, 30000);
    }, [stopWebCamera]);

    // ═══════════════════════════════════════════════════════════
    // NATIVE PATH (CameraPreview)
    // ═══════════════════════════════════════════════════════════

    const startNativeCamera = useCallback(async () => {
        try {
            // Guard: don't double-start
            const { value: running } = await CameraPreview.isCameraStarted();
            if (running) {
                nativeStartedRef.current = true;
                setNativePreviewActive(true);
                document.body.classList.add('native-camera-active');
                return;
            }

            await CameraPreview.start({
                position: 'rear',
                width: Math.floor(window.screen.width),
                height: Math.floor(window.screen.height),
                toBack: true,
                disableAudio: true,
                enableZoom: false,
                lockAndroidOrientation: true,
                x: 0,
                y: 0,
            });
            nativeStartedRef.current = true;
            setNativePreviewActive(true);
            setCameraError(null);
            // Make WebView transparent so camera behind it is visible
            document.body.classList.add('native-camera-active');
        } catch (err) {
            console.error('[CameraPreview] start failed:', err);
            setCameraError('لا يمكن فتح الكاميرا، يرجى التحقق من الصلاحيات.');
        }
    }, []);

    const stopNativeCamera = useCallback(async () => {
        try {
            await CameraPreview.stop();
        } catch (_) { /* already stopped */ }
        nativeStartedRef.current = false;
        setNativePreviewActive(false);
        setFlashEnabled(false);
        // Restore opaque WebView background
        document.body.classList.remove('native-camera-active');
    }, []);

    const toggleFlash = useCallback(async () => {
        try {
            const nextMode = flashEnabled ? 'off' : 'torch';
            await CameraPreview.setFlashMode({ flashMode: nextMode });
            setFlashEnabled((prev) => !prev);
        } catch {
            // device may not support torch — silently ignore
        }
    }, [flashEnabled]);

    // ═══════════════════════════════════════════════════════════
    // LIFECYCLE — visibility changes
    // ═══════════════════════════════════════════════════════════

    useEffect(() => {
        if (visible) {
            document.body.classList.add('document-camera-active');
            if (isNative()) {
                startNativeCamera();
            } else {
                startOrReattachWebCamera();
            }
        } else {
            document.body.classList.remove('document-camera-active');
            if (isNative()) {
                // On native: stop immediately — camera hardware is expensive
                stopNativeCamera();
            } else {
                // On web: keep stream warm for 30s for fast document step transitions
                scheduleStopWebCamera();
            }
        }
    }, [visible, startNativeCamera, stopNativeCamera, startOrReattachWebCamera, scheduleStopWebCamera]);

    // Final cleanup on unmount — always release hardware
    useEffect(() => {
        return () => {
            document.body.classList.remove('document-camera-active');
            if (isNative()) {
                CameraPreview.stop().catch(() => { });
                document.body.classList.remove('native-camera-active');
            } else {
                if (stopCameraTimeout.current) clearTimeout(stopCameraTimeout.current);
                stopWebCamera();
            }
        };
    }, [stopWebCamera]);

    // ═══════════════════════════════════════════════════════════
    // CAPTURE — shutter button
    // ═══════════════════════════════════════════════════════════

    const [isCapturing, setIsCapturing] = useState(false);
    const [permissionError, setPermissionError] = useState<string | null>(null);

    /**
     * Verify camera permission before opening the system camera via the native file input.
     * On Android, the WebView requires the CAMERA permission to be granted at OS level.
     * If denied, we surface a clear error message instead of silently failing (which was
     * causing the session to appear "closed" when Android threw an unhandled intent error).
     */
    const handleSystemCameraClick = useCallback(async () => {
        setPermissionError(null);
        try {
            // Check current permission state without prompting
            if ('permissions' in navigator) {
                const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
                if (status.state === 'denied') {
                    setPermissionError(
                        'تم رفض إذن الكاميرا. يرجى الذهاب إلى إعدادات الجوال ← التطبيقات ← أمين ← الأذونات وتفعيل الكاميرا.'
                    );
                    return;
                }
            }
        } catch {
            // permissions API not available on this device — proceed anyway
        }

        // Permission is granted (or unknown) — open native camera
        systemCameraInputRef.current?.click();
    }, []);

    // Native capture: CameraPreview.capture → base64 → File → compressImages → onImagesUploaded
    const handleNativeCapture = useCallback(async () => {
        if (isCapturing) {
            console.warn('📸 [FullScreenCamera] Capture already in progress, ignoring tap');
            return;
        }
        setIsCapturing(true);
        try {
            const result = await CameraPreview.capture({ quality: 85 });
            const file = base64ToFile(result.value, `doc_${Date.now()}.jpg`);

            try {
                const compressedFiles = await compressImages([file], {
                    maxSizeMB: 4,
                    maxWidthOrHeight: 2560,
                    quality: 0.92,
                });
                await onImagesUploaded(compressedFiles);
            } catch (error) {
                console.error('Compression failed:', error);
                await onImagesUploaded([file]);
            }
        } catch (err) {
            console.error('[CameraPreview] capture failed:', err);
        } finally {
            setIsCapturing(false);
        }
    }, [isCapturing, onImagesUploaded]);

    // Web capture: canvas-based (original implementation)
    const handleWebCapture = useCallback(async () => {
        const video = videoRef.current;
        const stream = streamRef.current;
        if (!video || !stream || video.videoWidth === 0) return;

        // Prevent double-tap from sending two concurrent uploads
        if (isCapturing) {
            console.warn('📸 [FullScreenCamera] Capture already in progress, ignoring tap');
            return;
        }
        setIsCapturing(true);

        // Scale to fit within CAPTURE_MAX_SIDE while maintaining aspect ratio
        const srcW = video.videoWidth;
        const srcH = video.videoHeight;
        const scale = Math.min(CAPTURE_MAX_SIDE / srcW, CAPTURE_MAX_SIDE / srcH, 1);
        const canvasW = Math.round(srcW * scale);
        const canvasH = Math.round(srcH * scale);

        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            setIsCapturing(false);
            return;
        }

        ctx.drawImage(video, 0, 0, canvasW, canvasH);

        canvas.toBlob(
            async (blob) => {
                if (!blob) {
                    setIsCapturing(false);
                    return;
                }
                const file = new File([blob], `document-${Date.now()}.jpg`, {
                    type: 'image/jpeg',
                });

                try {
                    const compressedFiles = await compressImages([file], {
                        maxSizeMB: 4,
                        maxWidthOrHeight: 2560,
                        quality: 0.92,
                    });
                    await onImagesUploaded(compressedFiles);
                } catch (error) {
                    console.error('Compression failed:', error);
                    await onImagesUploaded([file]);
                } finally {
                    setIsCapturing(false);
                }
            },
            'image/jpeg',
            CAPTURE_QUALITY,
        );
    }, [onImagesUploaded, isCapturing]);

    // Unified shutter handler
    const handleCapturePhoto = useCallback(async () => {
        if (isNative()) {
            await handleNativeCapture();
        } else {
            await handleWebCapture();
        }
    }, [handleNativeCapture, handleWebCapture]);

    const handleFileSelect = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        if (e.target) e.target.value = '';
        try {
            const compressedFiles = await compressImages(files, {
                maxSizeMB: 4,
                maxWidthOrHeight: 2560,
                quality: 0.92,
            });
            await onImagesUploaded(compressedFiles);
        } catch (error) {
            await onImagesUploaded(files);
        }
    };

    const handleClose = useCallback(() => {
        // Immediate full shutdown — user explicitly closed
        if (isNative()) {
            CameraPreview.stop().catch(() => { });
            nativeStartedRef.current = false;
            setNativePreviewActive(false);
            setFlashEnabled(false);
            document.body.classList.remove('native-camera-active');
        } else {
            if (stopCameraTimeout.current) clearTimeout(stopCameraTimeout.current);
            stopWebCamera();
        }
        onClose();
    }, [onClose, stopWebCamera]);

    // ── Render ──────────────────────────────────────────────
    // Hide the overlay UI immediately when not visible.
    // The camera hardware stream stays alive in the ref for 30s (web only).
    if (!visible) return null;

    return (
        <div className="fs-camera-overlay">
            {/* Gallery picker — no capture attribute so Android shows the full gallery */}
            <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
            {/* System camera — capture="environment" opens the native camera app directly */}
            <input
                ref={systemCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {cameraError ? (
                /* ── Error / no-permission fallback ── */
                <div className="fs-camera-error-state">
                    <button
                        className="fs-camera-close-top-btn"
                        onClick={handleClose}
                        aria-label="إغلاق"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                    <span className="material-symbols-outlined error-icon">
                        no_photography
                    </span>
                    <h3>{title}</h3>
                    <p className="error-msg">{cameraError}</p>

                    {/* Two explicit options when the in-app camera fails */}
                    {permissionError && (
                        <p className="fs-camera-permission-error">{permissionError}</p>
                    )}
                    <div className="fs-camera-action-row">
                        <button
                            className="fs-camera-action-btn"
                            onClick={handleSystemCameraClick}
                        >
                            <span className="material-symbols-outlined">photo_camera</span>
                            تصوير بالكاميرا
                        </button>
                        <button
                            className="fs-camera-action-btn"
                            onClick={() => galleryInputRef.current?.click()}
                        >
                            <span className="material-symbols-outlined">photo_library</span>
                            اختر من المعرض
                        </button>
                    </div>
                </div>
            ) : (
                /* ── Normal live-camera view ── */
                <>
                    {/* Video element — only rendered on web path */}
                    {!isNative() && (
                        <video
                            ref={videoRef}
                            className="fs-camera-video"
                            playsInline
                            muted
                            autoPlay
                        />
                    )}
                    <div className="fs-camera-ui">
                        <div className="fs-camera-top-bar">
                            {/* Flash toggle — native only */}
                            {isNative() && (
                                <button
                                    className="fs-camera-flash-btn"
                                    onClick={toggleFlash}
                                    aria-label={flashEnabled ? 'إيقاف الفلاش' : 'تشغيل الفلاش'}
                                >
                                    <span className="material-symbols-outlined">
                                        {flashEnabled ? 'flash_on' : 'flash_off'}
                                    </span>
                                </button>
                            )}
                            <button
                                className="fs-camera-close-btn"
                                onClick={handleClose}
                                aria-label="إغلاق"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="fs-camera-guide-wrapper">
                            <div className="fs-camera-guide-cutout"></div>
                            <p className="fs-camera-instruction">
                                ضع المستند داخل الإطار
                            </p>
                        </div>

                        <div className="fs-camera-bottom-controls">
                            <p className="fs-camera-title">{title}</p>

                            {/* Main shutter — uses native capture on Android, canvas on web */}
                            <button
                                className="fs-camera-take-btn"
                                onClick={handleCapturePhoto}
                                aria-label="التقط صورة"
                            ></button>

                            {/* Two secondary options clearly separated */}
                            {permissionError && (
                                <p className="fs-camera-permission-error">{permissionError}</p>
                            )}
                            <div className="fs-camera-action-row">
                                <button
                                    className="fs-camera-action-btn"
                                    onClick={handleSystemCameraClick}
                                >
                                    <span className="material-symbols-outlined">photo_camera</span>
                                    كاميرا الجوال
                                </button>
                                <button
                                    className="fs-camera-action-btn"
                                    onClick={() => galleryInputRef.current?.click()}
                                >
                                    <span className="material-symbols-outlined">photo_library</span>
                                    اختر من المعرض
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
